import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Wallet, MapPin, Plane, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD","EUR","GBP","SGD","AED","AUD","CAD","JPY","CHF","THB","MYR","HKD","NZD","ZAR","SAR","OMR","QAR","KWD","BHD"];
const CATEGORIES = ["Travel Stay","Food & Dining","Local Transport","Flights","Shopping","Cash Withdrawal","Forex Charges","Sightseeing","Communication","Medical","Other"];
const TXN_TYPES = ["purchase","atm_withdrawal","refund","reload","fee","reversal","adjustment"];

const fmtFC = (amount, ccy) => {
    if (amount == null) return "—";
    return `${ccy || ""} ${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ─── Forex Cards page ──────────────────────────────────────────────────────
export function ForexCards() {
    const [cards, setCards] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try { const r = await api.get("/forex/cards"); setCards(r.data); }
        catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="forex-cards-page">
            <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="eyebrow">Forex Cards</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Currency, unlocked.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">Prepaid multi-currency travel cards, wallet balances per currency, and every swipe abroad in native currency + INR.</p>
                </div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
                    <DialogTrigger asChild>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="forex-card-add">
                            <Plus size={14} className="mr-1.5" /> Add forex card
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit forex card" : "Add forex card"}</DialogTitle></DialogHeader>
                        <ForexCardForm initial={editing} onDone={() => { setOpen(false); setEditing(null); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>

            {cards.length === 0 ? (
                <div className="rounded-2xl border border-stone-200 bg-white p-14 text-center text-sm text-stone-500">
                    <Wallet size={22} className="mx-auto text-stone-400 mb-3" />
                    No forex cards yet. Add your first one — travel, subscriptions abroad, and any USD/EUR spend land here.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="forex-cards-grid">
                    {cards.map((c) => (
                        <ForexCardTile key={c.id} card={c} onEdit={() => { setEditing(c); setOpen(true); }} onDelete={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ForexCardTile({ card, onEdit, onDelete }) {
    const totals = (card.wallets || []).reduce(
        (a, w) => ({
            loaded: a.loaded + (w.loaded || 0),
            spent: a.spent + (w.spent || 0),
            available: a.available + ((w.loaded || 0) + (w.refunds || 0) - (w.spent || 0) - (w.fees || 0)),
        }),
        { loaded: 0, spent: 0, available: 0 }
    );
    const archive = async () => {
        if (!window.confirm(`Archive ${card.bank_name} · ${card.forex_card_name}? This won't delete history.`)) return;
        try { await api.delete(`/forex/cards/${card.id}`); toast.success("Archived"); onDelete?.(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={`forex-card-${card.id}`}>
            <div className="p-4 text-white" style={{ background: card.color_tag || "#0f4c81" }}>
                <div className="text-[10px] uppercase tracking-widest opacity-80">{card.bank_name}</div>
                <div className="font-display text-xl mt-1">{card.forex_card_name}</div>
                <div className="flex justify-between items-end mt-6">
                    <div className="font-mono-figure text-sm opacity-90">•••• {card.last_4}</div>
                    <div className="text-[11px] opacity-80">
                        {card.expiry_month?.toString().padStart(2, "0")}/{card.expiry_year?.toString().slice(-2)}
                    </div>
                </div>
            </div>
            <div className="p-4">
                <div className="flex justify-between text-xs mb-3">
                    <span className="text-stone-500">{card.cardholder_name}</span>
                    <span className={`uppercase tracking-widest text-[10px] font-semibold ${card.status === "Active" ? "text-emerald-700" : "text-stone-500"}`}>{card.status}</span>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">Wallets</div>
                {(card.wallets || []).length === 0 ? (
                    <div className="text-sm text-stone-500 mb-3">No wallets — add a reload to activate.</div>
                ) : (
                    <div className="space-y-1.5 mb-3">
                        {(card.wallets || []).map((w) => {
                            const avail = (w.loaded || 0) + (w.refunds || 0) - (w.spent || 0) - (w.fees || 0);
                            return (
                                <div key={w.currency} className="flex justify-between items-baseline text-sm">
                                    <span className="font-mono-figure text-stone-700">{w.currency}</span>
                                    <span className={`tabular-nums ${avail <= 0 ? "text-stone-400" : "text-stone-900 font-medium"}`}>{fmtFC(avail, w.currency)}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="grid grid-cols-3 border-t border-stone-100 pt-3 text-[11px]">
                    <div><div className="text-stone-500">Loaded</div><div className="tabular-nums">{fmtFC(totals.loaded, "")}</div></div>
                    <div><div className="text-stone-500">Spent</div><div className="tabular-nums">{fmtFC(totals.spent, "")}</div></div>
                    <div><div className="text-stone-500">Available</div><div className="tabular-nums font-medium">{fmtFC(totals.available, "")}</div></div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`forex-edit-${card.id}`}><Edit size={12} className="mr-1.5" /> Edit</Button>
                    <Button size="sm" variant="ghost" className="text-red-700" onClick={archive} data-testid={`forex-archive-${card.id}`}><Trash2 size={12} className="mr-1.5" /> Archive</Button>
                </div>
            </div>
        </div>
    );
}

function ForexCardForm({ initial, onDone }) {
    const [f, setF] = useState(() => initial || {
        forex_card_name: "", bank_name: "", card_type: "Multi-Currency", last_4: "",
        cardholder_name: "", supported_currencies: ["USD", "EUR", "GBP"],
        issue_date: "", expiry_month: "", expiry_year: "",
        reload_available: true, atm_withdrawal_enabled: true, online_usage_enabled: true, contactless_enabled: true,
        emergency_number: "", portal_link: "", status: "Active", remarks: "", color_tag: "#0f4c81",
    });
    const set = (k) => (e) => setF({ ...f, [k]: typeof e === "object" ? e.target?.value : e });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            const payload = { ...f, expiry_month: f.expiry_month ? Number(f.expiry_month) : null, expiry_year: f.expiry_year ? Number(f.expiry_year) : null };
            if (initial?.id) { await api.put(`/forex/cards/${initial.id}`, payload); toast.success("Saved"); }
            else { await api.post("/forex/cards", payload); toast.success("Added"); }
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const toggleCcy = (c) => {
        setF({ ...f, supported_currencies: f.supported_currencies.includes(c) ? f.supported_currencies.filter((x) => x !== c) : [...f.supported_currencies, c] });
    };
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Bank</Label><Input value={f.bank_name} onChange={set("bank_name")} data-testid="ff-bank" /></div>
                <div><Label>Card name</Label><Input value={f.forex_card_name} onChange={set("forex_card_name")} data-testid="ff-name" /></div>
                <div><Label>Cardholder</Label><Input value={f.cardholder_name} onChange={set("cardholder_name")} data-testid="ff-holder" /></div>
                <div><Label>Last 4 digits</Label><Input value={f.last_4} onChange={set("last_4")} maxLength={4} data-testid="ff-last4" /></div>
                <div>
                    <Label>Card type</Label>
                    <Select value={f.card_type} onValueChange={set("card_type")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Multi-Currency">Multi-Currency</SelectItem><SelectItem value="Single-Currency">Single-Currency</SelectItem></SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Status</Label>
                    <Select value={f.status} onValueChange={set("status")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{["Active","Inactive","Blocked","Expired","Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div><Label>Expiry month</Label><Input type="number" min="1" max="12" value={f.expiry_month} onChange={set("expiry_month")} data-testid="ff-exp-m" /></div>
                <div><Label>Expiry year</Label><Input type="number" min="2024" max="2050" value={f.expiry_year} onChange={set("expiry_year")} data-testid="ff-exp-y" /></div>
                <div><Label>Emergency number</Label><Input value={f.emergency_number || ""} onChange={set("emergency_number")} /></div>
                <div><Label>Bank portal link</Label><Input value={f.portal_link || ""} onChange={set("portal_link")} placeholder="https://..." /></div>
            </div>
            <div>
                <Label>Supported currencies</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {CURRENCIES.map((c) => (
                        <button key={c} type="button" onClick={() => toggleCcy(c)}
                                className={`text-xs px-2.5 py-1 rounded-full border ${f.supported_currencies.includes(c) ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-300 hover:border-stone-400"}`}
                                data-testid={`ff-ccy-${c}`}>{c}</button>
                    ))}
                </div>
            </div>
            <div>
                <Label>Remarks</Label>
                <Input value={f.remarks || ""} onChange={set("remarks")} />
            </div>
            <div className="flex justify-end gap-2 pt-3">
                <Button onClick={submit} disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="ff-submit">
                    {busy ? "Saving…" : (initial?.id ? "Save changes" : "Add forex card")}
                </Button>
            </div>
        </div>
    );
}

// ─── Forex Wallets page ────────────────────────────────────────────────────
export function ForexWallets() {
    const [data, setData] = useState({ per_card: [], by_currency: [] });
    useEffect(() => { api.get("/forex/wallets").then((r) => setData(r.data)).catch(() => {}); }, []);
    return (
        <div data-testid="forex-wallets-page">
            <div className="mb-6">
                <div className="eyebrow">Forex Wallets</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Every currency, one view.</h1>
                <p className="text-sm text-stone-500 mt-3">Live wallet balances aggregated across all active forex cards.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="wallets-by-currency">
                {data.by_currency.length === 0 && (
                    <div className="col-span-full rounded-2xl border border-stone-200 bg-white p-10 text-center text-sm text-stone-500">
                        No wallets active yet. Add a forex card and record a reload to see live balances here.
                    </div>
                )}
                {data.by_currency.map((w) => (
                    <div key={w.currency} className="rounded-xl border border-stone-200 bg-white p-4">
                        <div className="text-[11px] uppercase tracking-widest text-stone-500">{w.currency}</div>
                        <div className="font-display text-3xl tabular-nums mt-1">{fmtFC(w.available, "")}</div>
                        <div className="text-[11px] text-stone-500 mt-2">
                            Loaded {fmtFC(w.loaded, "")} · Spent {fmtFC(w.spent, "")} · Fees {fmtFC(w.fees, "")}
                        </div>
                    </div>
                ))}
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                <div className="px-5 py-3 border-b border-stone-100 text-sm font-medium text-stone-800">Per-card breakdown</div>
                {data.per_card.length === 0 ? (
                    <div className="p-6 text-sm text-stone-500 text-center">No active forex cards.</div>
                ) : (
                    <table className="dense-table w-full">
                        <thead><tr><th>Bank</th><th>Card</th><th>Last 4</th><th>Currency</th><th className="text-right">Loaded</th><th className="text-right">Spent</th><th className="text-right">Available</th></tr></thead>
                        <tbody>
                            {data.per_card.flatMap((c) => (c.wallets || []).map((w) => (
                                <tr key={`${c.card_id}-${w.currency}`}>
                                    <td>{c.bank_name}</td>
                                    <td>{c.forex_card_name}</td>
                                    <td className="font-mono-figure">••••{c.last_4}</td>
                                    <td className="font-mono-figure">{w.currency}</td>
                                    <td className="text-right tabular-nums">{fmtFC(w.loaded, "")}</td>
                                    <td className="text-right tabular-nums">{fmtFC(w.spent, "")}</td>
                                    <td className="text-right tabular-nums font-medium">{fmtFC(w.available, "")}</td>
                                </tr>
                            )))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// ─── Forex Expenses page ───────────────────────────────────────────────────
export function ForexExpenses() {
    const [expenses, setExpenses] = useState([]);
    const [cards, setCards] = useState([]);
    const [tripList, setTripList] = useState([]);
    const [open, setOpen] = useState(false);
    const load = useCallback(async () => {
        const [e, c, t] = await Promise.all([api.get("/forex/expenses"), api.get("/forex/cards"), api.get("/forex/trips")]);
        setExpenses(e.data); setCards(c.data); setTripList(t.data);
    }, []);
    useEffect(() => { load(); }, [load]);
    const cardMap = useMemo(() => Object.fromEntries(cards.map((c) => [c.id, c])), [cards]);
    const total = expenses.reduce((s, e) => s + (e.inr_equivalent || 0), 0);
    return (
        <div data-testid="forex-expenses-page">
            <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="eyebrow">Forex Expenses</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Every rupee, on the road.</h1>
                    <p className="text-sm text-stone-500 mt-3">Foreign-currency transactions with live INR equivalent and trip mapping.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="fx-exp-add">
                            <Plus size={14} className="mr-1.5" /> Record expense
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader><DialogTitle className="font-display text-2xl">New forex expense</DialogTitle></DialogHeader>
                        <ForexExpenseForm cards={cards} trips={tripList} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            <div className="text-sm text-stone-500 mb-4" data-testid="fx-exp-total">{expenses.length} entries · Total {fmtMoney(total)} INR equivalent</div>

            {expenses.length === 0 ? (
                <div className="rounded-2xl border border-stone-200 bg-white p-14 text-center text-sm text-stone-500">
                    No forex expenses yet. Record one manually — email-based auto-capture for forex is on the roadmap.
                </div>
            ) : (
                <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead><tr><th>Date</th><th>Merchant</th><th>Country</th><th>Card</th><th>Type</th><th className="text-right">Native</th><th className="text-right">INR eq.</th><th>Category</th><th>Trip</th></tr></thead>
                        <tbody>
                            {expenses.map((e) => (
                                <tr key={e.id} data-testid={`fx-exp-${e.id}`}>
                                    <td className="tabular-nums whitespace-nowrap">{fmtDate(e.date)}</td>
                                    <td>{e.merchant}</td>
                                    <td className="text-xs">{e.country || "—"}{e.city ? ` · ${e.city}` : ""}</td>
                                    <td className="text-xs">{cardMap[e.forex_card_id]?.bank_name || "—"} · ••••{cardMap[e.forex_card_id]?.last_4 || "----"}</td>
                                    <td className="text-xs">{e.txn_type}</td>
                                    <td className="text-right tabular-nums">{fmtFC(e.amount, e.currency)}</td>
                                    <td className="text-right tabular-nums">{e.inr_equivalent ? fmtMoney(e.inr_equivalent) : "—"}</td>
                                    <td className="text-xs">{e.category}</td>
                                    <td className="text-xs">{tripList.find((t) => t.id === e.trip_id)?.name || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ForexExpenseForm({ cards, trips, onDone }) {
    const [f, setF] = useState({
        forex_card_id: cards[0]?.id || "", date: new Date().toISOString().slice(0, 10), merchant: "",
        country: "", city: "", txn_type: "purchase",
        currency: "USD", amount: "", exchange_rate: "", inr_equivalent: "",
        bank_charges: "", category: "Travel Stay", trip_id: "", business_personal: "Personal", remarks: "",
    });
    const set = (k) => (e) => setF({ ...f, [k]: typeof e === "object" ? e.target?.value : e });
    const [busy, setBusy] = useState(false);
    const inrPreview = f.amount && f.exchange_rate ? Number(f.amount) * Number(f.exchange_rate) : null;
    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                ...f, amount: Number(f.amount || 0), exchange_rate: f.exchange_rate ? Number(f.exchange_rate) : null,
                inr_equivalent: f.inr_equivalent ? Number(f.inr_equivalent) : (inrPreview ? Number(inrPreview.toFixed(2)) : null),
                bank_charges: Number(f.bank_charges || 0), trip_id: f.trip_id || null,
            };
            await api.post("/forex/expenses", payload);
            toast.success("Recorded");
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={f.date} onChange={set("date")} /></div>
                <div>
                    <Label>Forex card</Label>
                    <Select value={f.forex_card_id} onValueChange={set("forex_card_id")}>
                        <SelectTrigger><SelectValue placeholder="Pick a card" /></SelectTrigger>
                        <SelectContent>{cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · ••••{c.last_4}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="col-span-2"><Label>Merchant</Label><Input value={f.merchant} onChange={set("merchant")} placeholder="e.g. Marriott London" /></div>
                <div><Label>Country</Label><Input value={f.country} onChange={set("country")} /></div>
                <div><Label>City</Label><Input value={f.city} onChange={set("city")} /></div>
                <div>
                    <Label>Transaction type</Label>
                    <Select value={f.txn_type} onValueChange={set("txn_type")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{TXN_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Currency</Label>
                    <Select value={f.currency} onValueChange={set("currency")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-64">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div><Label>Amount (foreign)</Label><Input type="number" step="0.01" value={f.amount} onChange={set("amount")} /></div>
                <div><Label>Exchange rate (1 unit → INR)</Label><Input type="number" step="0.0001" value={f.exchange_rate} onChange={set("exchange_rate")} placeholder="e.g. 83.20" /></div>
                <div>
                    <Label>INR equivalent {inrPreview && <span className="text-[10px] text-stone-500">(auto: {fmtMoney(inrPreview)})</span>}</Label>
                    <Input type="number" step="0.01" value={f.inr_equivalent} onChange={set("inr_equivalent")} placeholder={inrPreview ? String(inrPreview.toFixed(2)) : "0"} />
                </div>
                <div><Label>Bank charges (foreign)</Label><Input type="number" step="0.01" value={f.bank_charges} onChange={set("bank_charges")} /></div>
                <div>
                    <Label>Category</Label>
                    <Select value={f.category} onValueChange={set("category")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Trip (optional)</Label>
                    <Select value={f.trip_id || "none"} onValueChange={(v) => set("trip_id")(v === "none" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder="No trip" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">No trip</SelectItem>
                            {trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.country}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Business / Personal</Label>
                    <Select value={f.business_personal} onValueChange={set("business_personal")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Personal">Personal</SelectItem><SelectItem value="Business">Business</SelectItem></SelectContent>
                    </Select>
                </div>
            </div>
            <div className="flex justify-end pt-2">
                <Button onClick={submit} disabled={busy || !f.forex_card_id || !f.amount} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="fx-exp-submit">
                    {busy ? "Saving…" : "Record expense"}
                </Button>
            </div>
        </div>
    );
}

// ─── Forex Reloads page ────────────────────────────────────────────────────
export function ForexReloads() {
    const [rows, setRows] = useState([]);
    const [cards, setCards] = useState([]);
    const [open, setOpen] = useState(false);
    const load = useCallback(async () => {
        const [r, c] = await Promise.all([api.get("/forex/reloads"), api.get("/forex/cards")]);
        setRows(r.data); setCards(c.data);
    }, []);
    useEffect(() => { load(); }, [load]);
    const cardMap = useMemo(() => Object.fromEntries(cards.map((c) => [c.id, c])), [cards]);
    return (
        <div data-testid="forex-reloads-page">
            <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="eyebrow">Forex Reloads</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Every top-up, tracked.</h1>
                    <p className="text-sm text-stone-500 mt-3">Reload history with exchange rate, INR paid, bank charges, and GST.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="fx-reload-add">
                            <Plus size={14} className="mr-1.5" /> Record reload
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle className="font-display text-2xl">New reload</DialogTitle></DialogHeader>
                        <ForexReloadForm cards={cards} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {rows.length === 0 ? (
                <div className="rounded-2xl border border-stone-200 bg-white p-14 text-center text-sm text-stone-500">
                    No reloads recorded yet.
                </div>
            ) : (
                <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead><tr><th>Date</th><th>Card</th><th>Currency</th><th className="text-right">Loaded</th><th className="text-right">INR paid</th><th className="text-right">FX rate</th><th className="text-right">Charges</th><th className="text-right">GST</th><th>Ref</th></tr></thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <td className="tabular-nums whitespace-nowrap">{fmtDate(r.date)}</td>
                                    <td className="text-xs">{cardMap[r.forex_card_id]?.bank_name || "—"} · ••••{cardMap[r.forex_card_id]?.last_4 || "----"}</td>
                                    <td className="font-mono-figure">{r.currency}</td>
                                    <td className="text-right tabular-nums">{fmtFC(r.amount_loaded, r.currency)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(r.inr_paid)}</td>
                                    <td className="text-right tabular-nums">{r.exchange_rate}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(r.bank_charges || 0)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(r.gst || 0)}</td>
                                    <td className="text-xs font-mono-figure">{r.reference || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ForexReloadForm({ cards, onDone }) {
    const [f, setF] = useState({
        forex_card_id: cards[0]?.id || "", date: new Date().toISOString().slice(0, 10),
        currency: "USD", amount_loaded: "", inr_paid: "", exchange_rate: "",
        bank_charges: "", gst: "", source_account: "", reference: "", remarks: "",
    });
    const set = (k) => (e) => setF({ ...f, [k]: typeof e === "object" ? e.target?.value : e });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/forex/reloads", {
                ...f, amount_loaded: Number(f.amount_loaded || 0), inr_paid: Number(f.inr_paid || 0),
                exchange_rate: Number(f.exchange_rate || 0), bank_charges: Number(f.bank_charges || 0), gst: Number(f.gst || 0),
            });
            toast.success("Recorded"); onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={set("date")} /></div>
            <div>
                <Label>Card</Label>
                <Select value={f.forex_card_id} onValueChange={set("forex_card_id")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · ••••{c.last_4}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div>
                <Label>Currency</Label>
                <Select value={f.currency} onValueChange={set("currency")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div><Label>Amount loaded</Label><Input type="number" step="0.01" value={f.amount_loaded} onChange={set("amount_loaded")} /></div>
            <div><Label>INR paid</Label><Input type="number" step="0.01" value={f.inr_paid} onChange={set("inr_paid")} /></div>
            <div><Label>Exchange rate</Label><Input type="number" step="0.0001" value={f.exchange_rate} onChange={set("exchange_rate")} /></div>
            <div><Label>Bank charges (INR)</Label><Input type="number" step="0.01" value={f.bank_charges} onChange={set("bank_charges")} /></div>
            <div><Label>GST (INR)</Label><Input type="number" step="0.01" value={f.gst} onChange={set("gst")} /></div>
            <div><Label>Source account</Label><Input value={f.source_account} onChange={set("source_account")} placeholder="HDFC Savings · ••1234" /></div>
            <div className="col-span-2"><Label>Reference / UTR</Label><Input value={f.reference} onChange={set("reference")} /></div>
            <div className="col-span-2 flex justify-end">
                <Button onClick={submit} disabled={busy || !f.forex_card_id || !f.amount_loaded} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="fx-reload-submit">
                    {busy ? "Saving…" : "Record reload"}
                </Button>
            </div>
        </div>
    );
}

// ─── Trips page ─────────────────────────────────────────────────────────────
export function Trips() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const load = useCallback(async () => {
        const r = await api.get("/forex/trips"); setRows(r.data);
    }, []);
    useEffect(() => { load(); }, [load]);
    return (
        <div data-testid="trips-page">
            <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="eyebrow">Trips</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Every trip, every rupee.</h1>
                    <p className="text-sm text-stone-500 mt-3">Wrap forex expenses inside a trip to see budget vs. actual, currencies used, and leftover balance.</p>
                </div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
                    <DialogTrigger asChild>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="trip-add">
                            <Plus size={14} className="mr-1.5" /> New trip
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit trip" : "New trip"}</DialogTitle></DialogHeader>
                        <TripForm initial={editing} onDone={() => { setOpen(false); setEditing(null); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {rows.length === 0 ? (
                <div className="rounded-2xl border border-stone-200 bg-white p-14 text-center text-sm text-stone-500">
                    <Plane size={22} className="mx-auto text-stone-400 mb-3" />
                    No trips yet. Create one before you fly out — the dashboard will start bucketing forex spend automatically.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rows.map((t) => (
                        <div key={t.id} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`trip-card-${t.id}`}>
                            <div className="flex items-baseline justify-between">
                                <div>
                                    <div className="font-display text-2xl text-stone-900">{t.name}</div>
                                    <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1.5">
                                        <MapPin size={11} /> {t.country} · {fmtDate(t.start_date)} → {fmtDate(t.end_date)}
                                    </div>
                                </div>
                                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                                    t.status === "Active" ? "bg-emerald-100 text-emerald-800" :
                                    t.status === "Completed" ? "bg-stone-100 text-stone-700" :
                                    t.status === "Cancelled" ? "bg-red-100 text-red-800" :
                                    "bg-blue-100 text-blue-800"
                                }`}>{t.status}</span>
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                                <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Budget</div><div className="tabular-nums font-medium">{fmtMoney(t.budget_inr || 0)}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Actual</div><div className="tabular-nums font-medium">{fmtMoney(t.actual_spend_inr || 0)}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Left</div><div className={`tabular-nums font-medium ${(t.balance_left_inr || 0) < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtMoney(t.balance_left_inr || 0)}</div></div>
                            </div>
                            <div className="mt-3 text-[11px] text-stone-500">
                                {t.expense_count} expense{t.expense_count === 1 ? "" : "s"}
                                {t.currencies?.length ? ` · ${t.currencies.join(", ")}` : ""}
                                <span className="ml-2 uppercase tracking-widest">· {t.business_personal}</span>
                            </div>
                            <div className="mt-3 flex justify-end">
                                <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`trip-edit-${t.id}`}>
                                    <Edit size={12} className="mr-1.5" /> Edit
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TripForm({ initial, onDone }) {
    const [f, setF] = useState(() => initial || {
        name: "", country: "", start_date: "", end_date: "",
        budget_inr: "", business_personal: "Personal", status: "Planned", remarks: "",
        currencies: [], forex_card_ids: [],
    });
    const set = (k) => (e) => setF({ ...f, [k]: typeof e === "object" ? e.target?.value : e });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            const payload = { ...f, budget_inr: Number(f.budget_inr || 0) };
            if (initial?.id) { await api.put(`/forex/trips/${initial.id}`, payload); toast.success("Saved"); }
            else { await api.post("/forex/trips", payload); toast.success("Trip created"); }
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Trip name</Label><Input value={f.name} onChange={set("name")} placeholder="e.g. London Q1 sales visit" /></div>
            <div><Label>Country</Label><Input value={f.country} onChange={set("country")} /></div>
            <div>
                <Label>Status</Label>
                <Select value={f.status} onValueChange={set("status")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["Planned","Active","Completed","Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div><Label>Start date</Label><Input type="date" value={f.start_date} onChange={set("start_date")} /></div>
            <div><Label>End date</Label><Input type="date" value={f.end_date} onChange={set("end_date")} /></div>
            <div><Label>Budget (INR)</Label><Input type="number" step="0.01" value={f.budget_inr} onChange={set("budget_inr")} /></div>
            <div>
                <Label>Business / Personal</Label>
                <Select value={f.business_personal} onValueChange={set("business_personal")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Personal">Personal</SelectItem><SelectItem value="Business">Business</SelectItem></SelectContent>
                </Select>
            </div>
            <div className="col-span-2 flex justify-end">
                <Button onClick={submit} disabled={busy || !f.name || !f.country || !f.start_date} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="trip-submit">
                    {busy ? "Saving…" : (initial?.id ? "Save changes" : "Create trip")}
                </Button>
            </div>
        </div>
    );
}

// End of file
